import {
  Link as ChakraLink,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  StackProps,
  Text,
  VStack,
} from "@chakra-ui/react";
import axios from "axios";
import { useFormik } from "formik";
import { useState } from "react";
import { Link as ReactRouterLink, useNavigate } from "react-router-dom";
import { getCookie, setCookie } from "typescript-cookie";
import * as yup from "yup";
import { CurrentActiveUserInterface } from "../../constants/User";
import useCustomToast from "../../lib/useCustomToast";
import { requestFCMToken } from "../../services/firebase.config";
import { useGetUserLoginStore } from "../../store/useGetUserLoginStore";
import { saveFcmTokenToBackend } from "../../utils/helperFunction";
import { TerminalHelper } from "../../utils/TerminalHelper";
import { ButtonPrimary } from "../button/ButtonPrimary";
import { ForgotPasswordModal } from "../forgotpassword_page/ForgotPasswordModal";
import { PasswordInput } from "../input/PasswordInput";
import { useUserHeader } from "../../store/useUserHeader";

interface Props extends StackProps {
  title: string;
  subTitle: string;
  url: string;
  userType: number; // 0=buyer, 1=reseller, 2=supplier
}

const initialValues = {
  email: undefined,
  password: undefined,
};

export const LoginForm = ({
  title,
  subTitle,
  url,
  userType,
  ...props
}: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const toast = useCustomToast();
  const navigate = useNavigate();
  const { saveCurrentActiveUser, saveLoggedUsers } = useGetUserLoginStore();
  const { fetchUserHeader } = useUserHeader();

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: yup.object().shape({
      email: yup
        .string()
        .required("Email harus diisi")
        .email("Gunakan format email yang benar")
        .max(255, "Email tidak boleh lebih dari 255 karakter"),
      password: yup
        .string()
        .required("Password harus diisi")
        .max(32, "Password tidak boleh lebih dari 32 karakter")
        .matches(/[a-z]/, "Password harus mengandung huruf kecil")
        .matches(/[A-Z]/, "Password harus mengandung huruf besar")
        .matches(/[0-9]/, "Password harus mengandung angka"),
    }),
    onSubmit: async (values) => {
      setLoading(true);

      const payload = {
        email: values.email,
        password: values.password,
      };

      try {
        const responseLogin = await axios.post(
          `${process.env.REACT_APP_WEB_API_BASE_URL}${url}`,
          payload
        );

        const jwtToken = responseLogin.data.token.access_token;
        const userData = responseLogin?.data?.data;

        const vapidKey = `${process.env.REACT_APP_VAPID_KEY}`;

        let fcmToken: string | null = "";

        if (Notification.permission === "granted") {
          fcmToken = await requestFCMToken(vapidKey);
        }

        const responseFcmToken =
          userData?.fcm_token === fcmToken
            ? userData
            : Notification.permission === "granted"
            ? await saveFcmTokenToBackend(
                userData?.id,
                `${userData?.role}`,
                fcmToken
              )
            : userData;

        // save currentActiveUser
        saveCurrentActiveUser(responseFcmToken);

        // fetch user header
        fetchUserHeader(responseFcmToken?.id, responseFcmToken?.role);

        // add currentActiveUser to loggedUsers
        const userCookies = getCookie("loggedUsers") || "[]";
        let loggedUsers = JSON.parse(userCookies);

        const isUserLogged = loggedUsers.some(
          (user: CurrentActiveUserInterface) =>
            user?.id === responseFcmToken?.id &&
            user?.role === responseFcmToken?.role
        );

        if (!isUserLogged) {
          loggedUsers.push(responseFcmToken);
          saveLoggedUsers(loggedUsers);
        }

        // save jwt token and navigate
        if (userType === 0) {
          setCookie("buyerToken", jwtToken, { expires: 1, path: "/" });
          navigate("/buyer", { replace: true });
        } else if (userType === 1) {
          setCookie("resellerToken", jwtToken, { expires: 1, path: "/" });
          navigate("/reseller", { replace: true });
        } else {
          setCookie("supplierToken", jwtToken, { expires: 1, path: "/" });
          navigate("/supplier", { replace: true });
        }

        toast({
          title: responseLogin.data.message,
          status: "success",
        });
      } catch (err: any) {
        toast({
          title: err?.response?.data?.message,
        });
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <VStack w={"full"} {...props}>
      <Text
        fontWeight={"semibold"}
        fontSize={[18, null, 20]}
        textAlign={"center"}
      >
        {title}
      </Text>

      {/* <Text variant={"secondary"} textAlign={"center"} textStyle={"b-reg"}>
        {subTitle}
      </Text> */}

      <form
        id="loginForm"
        onSubmit={formik.handleSubmit}
        style={{ width: "100%", marginTop: "40px" }}
      >
        <VStack w={"100%"} gap={6}>
          <FormControl
            isInvalid={
              formik.errors.email && formik.touched.email ? true : false
            }
          >
            <FormLabel htmlFor="email">Email</FormLabel>
            <Input
              name="email"
              type="text"
              placeholder="email@gmail.com"
              onChange={formik.handleChange}
              value={formik.values.email || ""}
            />
            <FormErrorMessage>{formik.errors.email}</FormErrorMessage>
          </FormControl>

          <FormControl
            isInvalid={
              formik.errors.password && formik.touched.password ? true : false
            }
          >
            <FormLabel htmlFor="password">Password</FormLabel>
            <PasswordInput
              onConfirm={(inputValue) => {
                formik.setFieldValue("password", inputValue);
              }}
              placeholder="********"
            />

            <VStack align={"end"} mt={1}>
              <ForgotPasswordModal />
            </VStack>
            <FormErrorMessage>{formik.errors.password}</FormErrorMessage>
          </FormControl>

          <ButtonPrimary
            form="loginForm"
            isLoading={loading}
            loadingText="Loading"
            spinnerPlacement="start"
            type="submit"
            w={"100%"}
          >
            Login
          </ButtonPrimary>

          <HStack mt={4} fontSize={[12, null, 14]}>
            <Text>Belum mempunyai akun? </Text>

            <ChakraLink
              as={ReactRouterLink}
              to={"/register"}
              variant={"linkPrimary"}
            >
              Daftar
            </ChakraLink>
          </HStack>
        </VStack>
      </form>
    </VStack>
  );
};
